// Centralized AppFillip homepage content.
import {
  ChartLine,
  AppWindow,
  Megaphone,
  Star,
  BellRing,
  Brain,
  BarChart2,
  Users2,
  Newspaper,
  Zap,
  Rocket,
  Palette,
  Globe2,
  MessageSquare,
  Search,
  Compass,
  Wrench,
  Activity,
  TrendingUp,
  Globe,
  Layers,
  ShieldCheck,
  Sparkles,
  Gamepad2,
  Heart,
  Banknote,
  GraduationCap,
  ShoppingBag,
  UtensilsCrossed,
  Plane,
  Users,
  Music,
  Leaf,
  ListChecks,
  HeartHandshake,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Process", href: "#process" },
  { label: "Case Studies", href: "#case-studies" },
  { label: "Pricing", href: "#pricing" },
  { label: "Blog", href: "#blog" },
];

export const TRUST_BADGES = [
  { value: "100+", label: "Apps Ranked" },
  { value: "20+", label: "Markets Served" },
  { value: "50+", label: "App Categories" },
  { value: "4.9/5", label: "Client Rating" },
];

export const CLIENT_LOGOS = [
  "Studdy AI",
  "WageTap",
  "Crebit",
  "Porter",
  "XM Trading",
  "Amana",
  "Zee5",
  "BalaBalaCar",
  "Vimcal",
  "BabyBus",
  "MagicPin",
  "Instacart",
  "Groww",
];

export const SERVICES = [
  {
    icon: ChartLine,
    title: "App Store Optimization (ASO)",
    short: "ASO",
    slug: "aso",
    href: "/aso",
    description:
      "Keyword research, listing optimization, A/B tested screenshots and ratings strategy that move your app to page one of the App Store and Google Play.",
    bullets: ["Keyword strategy", "Listing & screenshots", "A/B testing"],
    span: "lg:col-span-2",
    accent: true,
  },
  {
    icon: Megaphone,
    title: "User Acquisition & Campaign Management",
    short: "UA",
    slug: "user-acquisition",
    href: "/user-acquisition",
    description:
      "Apple Search Ads, Google UAC, Meta and TikTok install campaigns managed end-to-end — paired with ASO so every paid dollar converts.",
    bullets: ["Apple Search Ads", "Google UAC", "Meta + TikTok"],
  },
  {
    icon: Users2,
    title: "Influencer & Creator Partnerships",
    short: "Creators",
    slug: "influencer-marketing",
    href: "/influencer-marketing",
    description:
      "Nano to mega — we source, negotiate and manage creator deals on TikTok, Instagram and YouTube that drive installs, not just views.",
    bullets: ["Creator sourcing", "UGC campaigns", "Performance deals"],
  },
  {
    icon: Newspaper,
    title: "Content Marketing & PR",
    short: "PR",
    slug: "content-marketing",
    href: "/content-marketing",
    description:
      "Editorial pitches, press releases, review site placements and SEO blog content that build authority and drive organic discovery.",
    bullets: ["Media outreach", "App store editorial", "SEO content"],
  },
  {
    icon: BellRing,
    title: "User Retention & Engagement",
    short: "Retention",
    slug: "app-retention",
    href: "/app-retention",
    description:
      "Push notifications, in-app messaging, onboarding optimisation and referral programs that keep users active beyond day 30.",
    bullets: ["Push & in-app", "Onboarding", "Referrals"],
  },
  {
    icon: BarChart2,
    title: "Analytics, Attribution & Measurement",
    short: "Data",
    slug: "analytics",
    href: "/analytics",
    description:
      "Firebase, Mixpanel and Amplitude set up correctly. SKAdNetwork, custom KPI dashboards and monthly performance reports.",
    bullets: ["Firebase · Mixpanel", "SKAdNetwork", "KPI dashboards"],
    span: "lg:col-span-2",
    accent: true,
  },
  {
    icon: Zap,
    title: "Advanced Paid Marketing",
    short: "Paid",
    slug: "advanced-paid-marketing",
    href: "/advanced-paid-marketing",
    description:
      "Programmatic display, DSP campaigns, retargeting, lookalike audiences and cross-channel spend optimisation beyond basic UA.",
    bullets: ["Programmatic / DSP", "Retargeting", "Cross-channel"],
  },
];

export const PREMIUM_SERVICES = [
  {
    title: "Go-to-Market (GTM) Strategy",
    short: "GTM",
    href: "/gtm-strategy",
    description: "Full launch planning, market research and go-live roadmap.",
  },
  {
    title: "Creative Production & Design",
    short: "Creative",
    href: "/creative-production",
    description: "Screenshots, preview videos, ad creatives and brand assets.",
  },
  {
    title: "International Expansion",
    short: "Global",
    href: "/international-expansion",
    description: "Localised ASO, regional UA and multi-market scaling.",
  },
  {
    title: "Consultancy With Expert",
    short: "Consult",
    href: "/consultancy",
    description: "1:1 strategy sessions, app audits and growth roadmaps.",
  },
];

export const LOGO_URL =
  "/u60qp48x_cropped-cropped-AppFillip-Logo-Black-Blue-Strip-3-scaled-1.webp";

export const FOOTER_LOGO_URL =
  "/u60qp48x_cropped-cropped-AppFillip-Logo-Black-Blue-Strip-3-scaled-1.webp";

export const WHY_US = [
  {
    icon: Globe2,
    title: "Global delivery, local expertise",
    text: "A worldwide team of ASO and growth specialists — delivering category-leading results at competitive global rates.",
  },
  {
    icon: Layers,
    title: "Dev + ASO, one roof",
    text: "We build the app and rank the app. No handoffs, no excuses, no ‘that’s not our team’.",
  },
  {
    icon: Globe,
    title: "15+ markets covered",
    text: "Localized ASO for USA, UK, AU, UAE, CA, DE, JP, SG, BR and more.",
  },
  {
    icon: Sparkles,
    title: "GEO-ready strategy",
    text: "We optimize beyond the App Store — into ChatGPT, Claude, Gemini and Perplexity answers.",
  },
  {
    icon: ShieldCheck,
    title: "Transparent reporting",
    text: "Live keyword ranks, conversion funnels and weekly performance memos. No black box.",
  },
  {
    icon: HeartHandshake,
    title: "50+ app categories",
    text: "Gaming, FinTech, EdTech, Health, Dating, eCom — we’ve shipped #1s across verticals.",
  },
];

export const APP_CATEGORIES = [
  { icon: Gamepad2, label: "Gaming" },
  { icon: Heart, label: "Dating" },
  { icon: Activity, label: "Health" },
  { icon: Banknote, label: "FinTech" },
  { icon: GraduationCap, label: "EdTech" },
  { icon: ShoppingBag, label: "eCommerce" },
  { icon: UtensilsCrossed, label: "Food Delivery" },
  { icon: Plane, label: "Travel" },
  { icon: Users, label: "Social" },
  { icon: Music, label: "Music" },
  { icon: Leaf, label: "Meditation" },
  { icon: ListChecks, label: "Productivity" },
];

export const PROCESS_STEPS = [
  {
    icon: Search,
    title: "Audit & Strategy",
    text: "Deep analysis of your app, market, competitors and channels to build a data-backed growth blueprint.",
  },
  {
    icon: TrendingUp,
    title: "Launch & Execute",
    text: "ASO, creatives, paid campaigns, influencers and content — all launched in parallel under one plan.",
  },
  {
    icon: Activity,
    title: "Measure & Optimise",
    text: "Real-time KPI dashboards, A/B testing and weekly iterations driven by what the data actually shows.",
  },
  {
    icon: Compass,
    title: "Scale & Win",
    text: "Double down on proven channels, expand to new markets and reach category leadership.",
  },
];

export const STATS = [
  { value: 320, suffix: "+", label: "Apps ranked on page 1" },
  { value: 15, suffix: "+", label: "Countries served" },
  { value: 50, suffix: "+", label: "App categories" },
  { value: 1.6, suffix: "★", label: "Avg. rating uplift", decimals: 1 },
  { value: 98, suffix: "%", label: "Client satisfaction" },
];

export const CASE_STUDIES = [
  {
    category: "FinTech",
    title: "From rank #38 to #2 on Google Play",
    metric: "+412%",
    metricLabel: "organic installs in 90 days",
    summary:
      "Re-engineered keyword stack, rebuilt screenshots, and shipped review reply playbook for a UAE neo-bank.",
    market: "UAE • Google Play",
  },
  {
    category: "Gaming",
    title: "Top 10 Casual chart, USA App Store",
    metric: "+267%",
    metricLabel: "lift in conversion rate",
    summary:
      "ASO + Apple Search Ads combo brought an indie casual game into the US Top 10 Casual chart.",
    market: "USA • App Store",
  },
  {
    category: "Health",
    title: "4.2 → 4.8 star rating in 60 days",
    metric: "−63%",
    metricLabel: "drop in negative reviews",
    summary:
      "Smart in-app prompts, onboarding rework and reply ops lifted ratings for a global meditation app.",
    market: "Global • iOS + Android",
  },
];

export const TESTIMONIALS = [
  {
    name: "Meera S.",
    role: "CMO · Neobank App",
    text: "We stopped relying on paid to drive volume. AppFillip’s ASO work made organic our #1 install channel in under three months.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=faces&fit=crop&w=400&h=400&q=85",
    caseStudy: { slug: "neobank-app-aso-growth",      metric: "+310%",  metricLabel: "organic installs"      },
  },
  {
    name: "James W.",
    role: "Head of Growth · Investment App",
    text: "Market-specific ASO is obvious in theory. AppFillip is the first agency that actually executed it — per country, per language, per regulatory nuance.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=faces&fit=crop&w=400&h=400&q=85",
    caseStudy: { slug: "investment-app-global-aso",   metric: "+265%",  metricLabel: "App Store visibility" },
  },
  {
    name: "Sophie L.",
    role: "Co-founder · Fitness App",
    text: "Influencer content that actually drove trials, not just views. AppFillip built the measurement layer that let us finally see which creators moved the needle.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=faces&fit=crop&w=400&h=400&q=85",
    caseStudy: { slug: "fitness-app-influencer-aso",  metric: "+188%",  metricLabel: "free trial starts"    },
  },
  {
    name: "Arjun V.",
    role: "CEO · EdTech Platform",
    text: "AppFillip moved every metric that mattered — impressions, organic ranks and install velocity — without forcing us to scale paid recklessly.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=faces&fit=crop&w=400&h=400&q=85",
    caseStudy: { slug: "learning-app-india-aso",      metric: "+220%",  metricLabel: "overall iOS growth"   },
  },
];

export const PRICING = [
  {
    tier: "Starter",
    badge: "Indie-friendly",
    price: "$299",
    originalPrice: "$599",
    limitedTime: true,
    cadence: "/month",
    duration: "3-month minimum commitment",
    accent: "#10b981",
    description:
      "Perfect entry tier for indie developers and bootstrapped founders who want to test app marketing without betting the company on it.",
    impact: [
      { value: "+90%", label: "organic installs" },
      { value: "+30%", label: "store CVR" },
      { value: "60 days", label: "to first lift" },
    ],
    expectedResults: [
      { metric: "+40–90%", label: "Organic installs in 90 days" },
      { metric: "Top 10", label: "Keyword rankings in 60 days" },
      { metric: "+25%", label: "Store page conversion rate" },
    ],
    features: [
      "ASO foundation — keyword research, metadata, copy",
      "Up to 5 A/B-tested screenshots (1 platform)",
      "Ratings & reviews ops setup",
      "Monthly performance memo",
      "Email support",
      "Free deep audit on day 1",
    ],
    idealFor:
      "Indie developers, solopreneurs, bootstrapped founders testing the waters",
    cta: "Start with Starter",
    highlighted: true,
  },
  {
    tier: "Custom",
    badge: "Talk to Sales",
    price: "Custom",
    cadence: "",
    duration: "Tailored to your stage and goals",
    accent: "#2196F3",
    description:
      "For funded startups, growth-stage apps and category leaders that need a multi-channel program — built around your KPIs, channels, markets and budget.",
    impact: [
      { value: "+280%", label: "installs" },
      { value: "+95%", label: "ROAS lift" },
      { value: "15+", label: "markets covered" },
    ],
    features: [
      "Everything in Starter, plus:",
      "Multi-channel UA (ASA · UAC · Meta · TikTok · YouTube)",
      "Influencer marketing + creator whitelisting",
      "Lifecycle messaging (push · in-app · email)",
      "Advanced analytics + MMP audit + dashboards",
      "Localization for 5+ markets",
      "Dedicated strategist + Slack channel",
      "Quarterly business review",
    ],
    idealFor:
      "Funded startups, growth-stage apps, multi-market publishers, enterprise app teams",
    cta: "Talk to Sales",
    highlighted: false,
  },
];

export const BLOG_POSTS = [
  {
    category: "ASO Playbook",
    title: "App Store Optimization in 2025: the keyword stack that still works",
    excerpt:
      "Why long-tail keyword clustering beats single-keyword chasing on the modern App Store algorithm.",
    readTime: "8 min read",
    cover:
      "https://images.unsplash.com/photo-1770013277247-ab7a08d3d9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    category: "Growth",
    title: "From rank #50 to #1: a 90-day ASO sprint, fully open-sourced",
    excerpt:
      "Inside the screenshot, metadata and review-loop changes we ran for a UAE FinTech client.",
    readTime: "11 min read",
    cover:
      "https://images.unsplash.com/photo-1770210217380-d78a69acdc77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
  },
  {
    category: "GEO",
    title: "Generative Engine Optimization for apps: the new ASO frontier",
    excerpt:
      "How to get your app cited by ChatGPT, Claude, Gemini and Perplexity — and why it matters in 2025.",
    readTime: "9 min read",
    cover:
      "https://images.pexels.com/photos/13597064/pexels-photo-13597064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const MARKETS = [
  "USA",
  "United Kingdom",
  "Australia",
  "UAE",
  "Canada",
  "Germany",
  "Japan",
  "Singapore",
  "Brazil",
  "India",
];

export const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/1de62c85-d46e-42c6-a01d-bc940b22809d/images/e9584472188d7eb7c84c1053e2153017c49efbe341ff5d1aa8fb942fbdb5ef12.png";

export const GROWTH_ABSTRACT =
  "https://static.prod-images.emergentagent.com/jobs/1de62c85-d46e-42c6-a01d-bc940b22809d/images/3578074adbee8e5cb08627e35dd55206ea0b874e8ba66c6ff73e0b4787330848.png";
